---
title: "The Entire CLI Is Now Integrated with Grok"
description: "Save your Grok Build sessions with Entire's external agent plugin, so the prompts, responses, and tool calls behind every commit stay in your Git history."
category: Changelog
author: Lizzie Siegle
---

<CloudflareStreamVideo
  videoId="d77d7f9ecf9ffbd05b1b857716ef5b3d"
  title="Grok bots deploying to Entire from the Entire CLI"
  bordered={false}
/>

You can now capture your Grok Build sessions with the Entire CLI using an [external agent plugin](https://github.com/entireio/external-agents/tree/main/agents/entire-agent-grok). The plugin connects your conversation to your commits so you and your teammates can review the prompts, responses, and tool calls behind code changes, creating a loop of continuous memory across the production cycle.

Conversations are stored in your Git history, right alongside your code itself.

Below, you can run each terminal command directly or ask your agent in natural language to run it for you.

## Install the Plugin

You’ll need [Entire](https://docs.entire.io/installation), [Grok Build](https://x.ai/build), [Go](https://go.dev/doc/install), and [mise](https://mise.jdx.dev/getting-started.html) installed.

First, clone the external agents repository and build the Grok plugin:

```shell
git clone https://github.com/entireio/external-agents.git
cd external-agents/agents/entire-agent-grok
mise run build
```

Then put the binary on your PATH:

```shell
mkdir -p ~/.local/bin
cp entire-agent-grok ~/.local/bin/
export PATH="$HOME/.local/bin:$PATH"
command -v entire-agent-grok
```

That last command prints the binary location. The export applies to your current terminal session. If `~/.local/bin` is not on your PATH, add it to your shell configuration.

Alternatively, skip the clone and install the plugin with Go:

```shell
go install github.com/entireio/external-agents/agents/entire-agent-grok/cmd/entire-agent-grok@latest
command -v entire-agent-grok
```

This puts the binary in `~/go/bin`. If `command -v` prints nothing, add `~/go/bin` to your PATH.

## Connect Grok Build to Your Repo

Switch to the repository where you want to capture sessions and enable Entire with the Grok agent:

```shell
cd /path/to/your/repo
entire enable --agent grok
```

If Entire is already enabled in the repo, you can add Grok with `entire agent add grok` (CLI v0.11.6+).

Either command enables Entire in the repository if it isn’t already, installs Grok’s hooks in `.grok/hooks/entire.json`, and turns on `external_agents` in `.entire/settings.local.json`, which Entire’s `.entire/.gitignore` keeps out of Git.

## Trust the Hooks

Launch Grok by running:

```shell
grok
```

When Grok asks whether you trust the contents of this directory, choose **Yes**. If you already dismissed that prompt, run this inside Grok:

```text
/hooks-trust
```

Grok skips project hooks until you trust the folder, but it doesn’t warn you when it skips them. If your session produces no checkpoints, be sure to check folder trust!

## Give Grok a Task and Commit the Change

For a small test, ask Grok something like:

```text
Create hello.txt containing "hello world". Don’t commit the change yet.
```

When Grok finishes, return to your shell, review the change, and commit it:

```shell
git add hello.txt
git diff --cached
git commit -m "Add hello world"
```

Entire writes the checkpoint when you commit. Run `entire session list` to see your Grok Build session and its checkpoint.

## Read the Conversation That Led to the Commit

Inspect the checkpoint associated with your latest commit:

```shell
entire checkpoint explain --commit HEAD --full
```

Reading the transcript, you can follow what you asked Grok to do and how the model responded. Later on, when a teammate asks why `hello.txt` exists, you can both read and reference the exact session that produced it.

For your project, replace the test task with your actual task and commit using standard Git commands.

## Connect Grok Bot with Your Own Plugin

Grok Bot, the desktop app, doesn’t run agent hooks, so it isn’t supported out of the box. You can connect it by writing an `entire-agent-grokbot` plugin that implements the [external agent protocol](https://github.com/entireio/cli/blob/main/docs/architecture/external-agent-protocol.md) and reports each bot’s session events to Entire. Entire picks up any `entire-agent-<name>` binary on your PATH, so once your plugin is there, add it with `entire agent add grokbot`.

## Try It in Your Existing Repo

Entire records other agents the same way, for example `entire agent add claude-code` or `entire agent add codex`.

Pick a small task you already planned to give Grok. After committing, open the checkpoint and check whether the session gives a teammate enough context to understand the change.
